import React, { useEffect, useMemo, useState } from 'react';
import {
  MessageSquare, CheckCircle2, Clock, XCircle, AlertTriangle, Info, Phone as PhoneIcon,
  ChevronDown, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';

// State machine surfaced by the backend `workflow_state`.
const STATE_META = {
  unsubmitted: { label: 'Not started', tone: 'neutral', icon: Info },
  pending_brand_review: { label: 'Brand review in progress', tone: 'pending', icon: Clock },
  pending_campaign_review: { label: 'Campaign review in progress', tone: 'pending', icon: Clock },
  approved_active: { label: 'Text messaging active', tone: 'success', icon: CheckCircle2 },
  rejected: { label: 'Registration failed', tone: 'danger', icon: XCircle },
};

const TONE_CLASS = {
  neutral: 'bg-brand-cream border-brand-green/10 text-brand-green',
  pending: 'bg-amber-50 border-amber-200 text-amber-900',
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  danger: 'bg-red-50 border-red-200 text-red-900',
};

function describeError(e) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || detail.error || JSON.stringify(detail);
  return e?.message || 'Something went wrong';
}

function digits(s) { return String(s || '').replace(/\D/g, ''); }

function maskedTaxId(value, focused, entity) {
  if (focused) return value;
  const d = digits(value);
  if (!d) return '';
  const label = entity === 'business' ? 'EIN' : 'SSN';
  if (d.length <= 4) return d;
  return `${label} •••••${d.slice(-4)}`;
}

function StatusBanner({ state, message, reason }) {
  const meta = STATE_META[state] || STATE_META.unsubmitted;
  const Icon = meta.icon;
  return (
    <div className={`rounded-lg border px-4 py-3 flex items-start gap-3 ${TONE_CLASS[meta.tone]}`}
         data-testid="a2p-status-banner">
      <Icon className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="font-semibold text-sm">{meta.label}</div>
        {message && <div className="text-xs mt-0.5 opacity-80 break-words">{message}</div>}
        {reason && state === 'rejected' && (
          <div className="text-xs mt-1 font-medium">Reason: {reason}</div>
        )}
      </div>
    </div>
  );
}

function VoiceReadyBanner() {
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50 text-sky-900 px-4 py-3 flex items-start gap-3"
         data-testid="a2p-voice-banner">
      <PhoneIcon className="w-5 h-5 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-sm">Voice Calls Are Ready to Use</div>
        <div className="text-xs mt-0.5 opacity-90">
          Your assigned AI Assistant phone number is fully functional for incoming and outgoing
          voice calls right now. No setup required.
        </div>
      </div>
    </div>
  );
}

function ReviewTimelineDisclaimer() {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 flex items-start gap-3"
         data-testid="a2p-timeline-disclaimer">
      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="text-xs">
        <span className="font-semibold">Important Note:</span> Once submitted, carrier review and
        manual campaign approval typically take up to <b>15 business days</b> (roughly two weeks).
        We'll email you and show a dashboard notification the moment your texting feature is
        unlocked.
      </div>
    </div>
  );
}

function ComplianceForm({ onSubmitted }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [taxFocused, setTaxFocused] = useState(false);
  const [form, setForm] = useState({
    entity_type: 'business',
    legal_name: '',
    tax_id: '',
    street: '', unit: '', city: '', state: '', zip: '',
    website_url: '',
    contact_email: '',
    contact_phone: '',
  });

  const taxLabel = form.entity_type === 'business' ? 'EIN (9 digits)' : 'SSN (9 digits)';
  const legalLabel = form.entity_type === 'business'
    ? 'Legal Business Name'
    : 'Legal Full Name (First Last)';

  const change = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    // Client-side sanity check — backend re-validates.
    if (digits(form.tax_id).length !== 9) {
      toast({ title: 'Tax ID must be 9 digits', variant: 'destructive' });
      return;
    }
    if (!/^https?:\/\//i.test(form.website_url.trim())) {
      toast({ title: 'Website URL must start with http:// or https://', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        entity_type: form.entity_type,
        legal_name: form.legal_name.trim(),
        tax_id: digits(form.tax_id),
        address: {
          street: form.street.trim(), unit: form.unit.trim(),
          city: form.city.trim(), state: form.state.trim().toUpperCase(),
          zip: form.zip.trim(),
        },
        website_url: form.website_url.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
      };
      const { data } = await api.post('/a2p/submit', payload);
      toast({ title: 'Submitted for carrier review' });
      onSubmitted?.(data);
    } catch (e) {
      toast({ title: 'Submission failed', description: describeError(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 space-y-5" data-testid="a2p-compliance-form">
      <div>
        <div className="text-sm font-semibold text-brand-green">Compliance details</div>
        <p className="text-xs text-brand-green/70 mt-1">
          Everything is encrypted at rest. Your tax identifier is never shown in full after
          submission.
        </p>
      </div>

      {/* Entity type */}
      <div className="space-y-2">
        <Label>Business type</Label>
        <RadioGroup value={form.entity_type} onValueChange={(v) => change('entity_type', v)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label htmlFor="entity-business"
                 className="border rounded-lg p-3 cursor-pointer flex items-start gap-2 hover:bg-brand-cream/50">
            <RadioGroupItem value="business" id="entity-business" data-testid="a2p-entity-business" />
            <div>
              <div className="text-sm font-medium text-brand-green">Registered Business</div>
              <div className="text-[11px] text-brand-green/70">LLC, Inc, Corp, PC — you'll enter an EIN.</div>
            </div>
          </label>
          <label htmlFor="entity-soleprop"
                 className="border rounded-lg p-3 cursor-pointer flex items-start gap-2 hover:bg-brand-cream/50">
            <RadioGroupItem value="sole_prop" id="entity-soleprop" data-testid="a2p-entity-soleprop" />
            <div>
              <div className="text-sm font-medium text-brand-green">Sole Proprietor / Independent</div>
              <div className="text-[11px] text-brand-green/70">Individual real-estate agent — you'll enter your SSN.</div>
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* Legal name + Tax ID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="a2p-legal-name">{legalLabel}</Label>
          <Input id="a2p-legal-name" data-testid="a2p-legal-name" value={form.legal_name}
                 onChange={(e) => change('legal_name', e.target.value)}
                 placeholder={form.entity_type === 'business' ? 'John Doe Real Estate LLC' : 'John Doe'} />
        </div>
        <div>
          <Label htmlFor="a2p-tax-id">{taxLabel}</Label>
          <Input id="a2p-tax-id" data-testid="a2p-tax-id"
                 type={taxFocused ? 'text' : 'text'}
                 inputMode="numeric"
                 maxLength={11}
                 value={taxFocused ? form.tax_id : maskedTaxId(form.tax_id, false, form.entity_type)}
                 onFocus={() => setTaxFocused(true)}
                 onBlur={() => setTaxFocused(false)}
                 onChange={(e) => change('tax_id', digits(e.target.value).slice(0, 9))}
                 placeholder="123456789" />
        </div>
      </div>

      {/* Address */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
        <div>
          <Label htmlFor="a2p-street">Street address</Label>
          <Input id="a2p-street" data-testid="a2p-street" value={form.street}
                 onChange={(e) => change('street', e.target.value)} placeholder="123 Agent Way" />
        </div>
        <div>
          <Label htmlFor="a2p-unit">Unit / Suite (optional)</Label>
          <Input id="a2p-unit" data-testid="a2p-unit" value={form.unit}
                 onChange={(e) => change('unit', e.target.value)} placeholder="Suite A" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-3">
        <div>
          <Label htmlFor="a2p-city">City</Label>
          <Input id="a2p-city" data-testid="a2p-city" value={form.city}
                 onChange={(e) => change('city', e.target.value)} placeholder="Roseville" />
        </div>
        <div>
          <Label htmlFor="a2p-state">State</Label>
          <Input id="a2p-state" data-testid="a2p-state" value={form.state}
                 onChange={(e) => change('state', e.target.value.toUpperCase().slice(0, 2))}
                 placeholder="CA" maxLength={2} />
        </div>
        <div>
          <Label htmlFor="a2p-zip">ZIP</Label>
          <Input id="a2p-zip" data-testid="a2p-zip" value={form.zip}
                 onChange={(e) => change('zip', e.target.value)} placeholder="95678" />
        </div>
      </div>

      {/* Website + contact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="a2p-website">Website / Landing page URL</Label>
          <Input id="a2p-website" data-testid="a2p-website" value={form.website_url}
                 onChange={(e) => change('website_url', e.target.value)}
                 placeholder="https://your-realtor-site.com/opt-in" />
        </div>
        <div>
          <Label htmlFor="a2p-email">Contact email</Label>
          <Input id="a2p-email" data-testid="a2p-email" value={form.contact_email}
                 onChange={(e) => change('contact_email', e.target.value)}
                 placeholder="agent@example.com" type="email" />
        </div>
      </div>
      <div>
        <Label htmlFor="a2p-phone">Contact phone (E.164)</Label>
        <Input id="a2p-phone" data-testid="a2p-phone" value={form.contact_phone}
               onChange={(e) => change('contact_phone', e.target.value)}
               placeholder="+15551234567" />
      </div>

      <div className="pt-2">
        <Button onClick={submit} disabled={busy} data-testid="a2p-submit"
                className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Submit for review'}
        </Button>
      </div>
    </Card>
  );
}

export default function CrmSettingsTextMessaging() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/a2p/status');
      setStatus(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const state = status?.workflow_state || 'unsubmitted';
  const inProgress = useMemo(
    () => ['pending_brand_review', 'pending_campaign_review'].includes(state),
    [state],
  );

  return (
    <div className="space-y-6" data-testid="crm-text-messaging">
      <div>
        <h2 className="font-serif text-2xl text-brand-green">Activate Text Messaging</h2>
        <p className="text-sm text-brand-green/70 mt-1">
          Add outbound SMS to your AI Assistant with a one-time carrier compliance filing.
        </p>
      </div>

      <VoiceReadyBanner />

      <p className="text-sm text-brand-green/80 leading-relaxed">
        To unlock outbound text messaging capabilities for your AI Assistant, mobile carriers
        require a one-time business verification process (A2P 10DLC compliance). This ensures
        your messages are securely delivered and whitelisted by major networks.
      </p>

      <ReviewTimelineDisclaimer />

      {loading ? (
        <div className="flex items-center gap-2 text-brand-green/70 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading status…
        </div>
      ) : (
        <>
          {state !== 'unsubmitted' && (
            <StatusBanner
              state={state}
              message={status?.last_twilio_message}
              reason={status?.rejection_reason}
            />
          )}

          {state === 'unsubmitted' && !showForm && (
            <Button onClick={() => setShowForm(true)} data-testid="a2p-open-form"
                    className="bg-brand-gold text-brand-green hover:bg-brand-gold/90">
              Fill Out Compliance Form <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          )}

          {state === 'rejected' && !showForm && (
            <Button onClick={() => setShowForm(true)} data-testid="a2p-retry"
                    className="bg-brand-green text-brand-cream hover:bg-brand-green/90">
              Fix &amp; resubmit
            </Button>
          )}

          {showForm && (
            <ComplianceForm onSubmitted={(row) => { setStatus(row); setShowForm(false); }} />
          )}

          {inProgress && status?.legal_name && (
            <Card className="p-4 text-sm text-brand-green/80" data-testid="a2p-summary-card">
              <div className="flex items-center gap-2 mb-2 text-brand-green">
                <MessageSquare className="w-4 h-4 text-brand-gold" />
                <div className="font-semibold">Submission on file</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div><span className="text-brand-green/60">Legal name:</span> {status.legal_name}</div>
                <div><span className="text-brand-green/60">Entity:</span> {status.entity_type === 'business' ? 'Registered Business' : 'Sole Proprietor'}</div>
                <div><span className="text-brand-green/60">Tax ID:</span> •••••{status.tax_id_last4}</div>
                <div><span className="text-brand-green/60">Website:</span> <a className="underline" href={status.website_url} target="_blank" rel="noreferrer">{status.website_url}</a></div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
